// D2Agent.js
import React, {
    useState,
    useEffect,
    useRef,
    useCallback,
    useMemo,
  } from 'react';
import axios from 'axios';
import D2TalkingHeadAvatar from './D2TalkingHeadAvatar';

import config from './config';

import MicIcon from '@mui/icons-material/Mic';
import SendIcon from '@mui/icons-material/Send';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';

/* MOBILE + DESKTOP RESPONSIVE STYLES (injected below via style tag in return) */

const RESOURCE_LINKS = [
  {
    label: 'OSF PDM Models',
    href: 'https://osf.io/ft9yp/overview',
    opensInNewTab: true,
  },
  {
    label: 'Asynchronous vs Synchronous',
    href: `${config.API_BASE_URL}/resources/asynchronous-vs-synchronous.pdf`,
    download: 'Asynchronous vs Synchronous.pdf',
  },
  {
    label: 'D2 Clinic Presentation only PDM',
    href: `${config.API_BASE_URL}/resources/d2-clinic-presentation-only-pdm.pptx`,
    download: 'D2 Clinic Presentation only PDM.pptx',
  },
  {
    label: 'QDRS',
    href: 'https://umiamibrainhealth.org/downloads/the-quick-dementia-rating-system-qdrs-patient-and-informant-versions/',
    opensInNewTab: true,
  },
];
  
  const SpeechRecognition =
    window.SpeechRecognition || window.webkitSpeechRecognition;
  const speechSynthesis = window.speechSynthesis;
  
  // Determine background URL from localStorage or default
  const getBackgroundUrl = () => {
    return (
      localStorage.getItem('background') ||
      'https://storage.googleapis.com/vicaassets/1.png'
    );
  };
  
  function D2Agent() {
    const [conversation, setConversation] = useState([]);
    const [isListening, setIsListening] = useState(false);
    const [isSpeaking, setIsSpeaking] = useState(false);
    const [statusMessage, setStatusMessage] = useState('');
    const [error, setError] = useState('');
    const [textInput, setTextInput] = useState('');
    const [sessionStarted, setSessionStarted] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [speechRecognitionSupported, setSpeechRecognitionSupported] =
      useState(false);
  
    const url = `${config.API_BASE_URL}/api/d2`;
    const recognition = useRef(null);
    const conversationEndRef = useRef(null);
    const talkingHeadRef = useRef(null);
  
    useEffect(() => {
      setSpeechRecognitionSupported(!!SpeechRecognition);
      if (!SpeechRecognition) {
        setError('Speech Recognition not supported in this browser.');
      }
    }, []);
  
    useEffect(() => {
      conversationEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [conversation]);
  
    const speakText = useCallback((text) => {
      // Stop any ongoing speech when new speech begins
      if (speechSynthesis && speechSynthesis.speaking) {
        speechSynthesis.cancel();
      }
      if (!text || !talkingHeadRef.current) return;
  
      try {
        talkingHeadRef.current.speakText(text);
        setIsSpeaking(true);
        setStatusMessage('Avatar speaking...');
        // Basic timeout to reset UI state; adjust if needed.
        setTimeout(() => {
          setIsSpeaking(false);
          setStatusMessage(
            isListening ? 'Listening...' : ''
          );
        }, 2000);
      } catch (err) {
        console.error('Error speaking text through avatar:', err);
        setError('Error speaking response.');
        setIsSpeaking(false);
        setStatusMessage('');
      }
    }, [isListening]);
  
    const handleToggleListen = () => {
      if (!speechRecognitionSupported || isSpeaking) return;
  
      if (isListening) {
        setTimeout(() => {
          recognition.current?.stop();
        }, 3000); // 3‑second delay before stopping mic
        setIsListening(false);
        setStatusMessage('');
      } else {
        recognition.current = new SpeechRecognition();
        recognition.current.continuous = false;
        recognition.current.interimResults = false;
        recognition.current.lang = 'en-US';
  
        recognition.current.onstart = () => {
          setIsListening(true);
          setStatusMessage('Listening... Speak now');
          setError('');
  
          if (speechSynthesis && speechSynthesis.speaking) {
            speechSynthesis.cancel();
            setIsSpeaking(false);
          }
        };
  
        recognition.current.onresult = (event) => {
          const transcript = event.results[event.results.length - 1][0].transcript.trim();
          if (transcript) {
            sendMessageToBackend(transcript);
          } else {
            setStatusMessage('Did not hear anything clearly.');
          }
        };
  
        recognition.current.onerror = (event) => {
          console.error('SpeechRecognition Error:', event.error);
          setError(`Speech recognition error: ${event.error}`);
          setIsListening(false);
          setStatusMessage('Recognition error');
        };
  
        recognition.current.onend = () => {
          if (isListening && !error && statusMessage !== '') {
            setStatusMessage('');
          }
          setIsListening(false);
        };
  
        try {
          recognition.current.start();
        } catch (err) {
          console.error('Error starting recognition:', err);
          setError(`Could not start listening: ${err.message}`);
          setIsListening(false);
          setStatusMessage('Mic start error');
        }
      }
    };
  
    const sendMessageToBackend = async (userText) => {
      // Interrupt any ongoing avatar speech when user sends a new message
      if (speechSynthesis && speechSynthesis.speaking) {
        speechSynthesis.cancel();
      }
      setIsSpeaking(false);
      if (!userText) return;
      setIsLoading(true);

      setIsListening(false);
      recognition.current?.stop();
      setStatusMessage('');
      setError('');

      const newUserMessage = {
        role: 'user',
        text: userText,
        createdAt: new Date().toISOString(),
      };
      const updatedConversation = [...conversation, newUserMessage];
      setConversation(updatedConversation);

      try {
        const response = await axios.post(url, {
          userMessage: userText,
        });

        if (response.data.text) {
          const text = response.data.text;
          const modelResponse = {
            role: 'model',
            text,
            createdAt: new Date().toISOString(),
          };
          setConversation((prev) => [...prev, modelResponse]);
          setIsLoading(false);
          speakText(text);
        } else {
          setIsLoading(false);
          setError(
            response.data.details || 'Received empty response from server.'
          );
          setStatusMessage('Response error');
        }
      } catch (err) {
        setIsLoading(false);
        console.error('Error sending message:', err);
        setError(
          `Failed to get response: ${
            err.response?.data?.error || err.message || 'Network error'
          }`
        );
        setStatusMessage('Error communicating');
      } finally {
        setIsLoading(false);
        if (!isSpeaking && !error) {
          setStatusMessage('');
        }
      }
    };
  
    const handleStartSession = async () => {
      setSessionStarted(true);
      setStatusMessage('Getting introduction...');
      setError('');
  
      try {
        const response = await axios.post(url, {
          userMessage: 'hello',
        });
        if (response.data.text) {
          const text = response.data.text;
          setConversation([
            { role: 'model', text, createdAt: new Date().toISOString() },
          ]);
          speakText(text);
        }
      } catch (err) {
        console.error('Error fetching greeting:', err);
        setError(`Failed to connect to server: ${err.message}`);
        setStatusMessage('Connection error.');
      }
    };
  
    const formatDateHeader = (isoDate) => {
      const date = new Date(isoDate);
      const today = new Date();
      const diffTime = today - date;
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7)
        return date.toLocaleDateString(undefined, { weekday: 'long' });
      const options =
        diffDays < 365
          ? { month: 'long', day: 'numeric' }
          : { year: 'numeric', month: 'long', day: 'numeric' };
      return date.toLocaleDateString(undefined, options);
    };
  
    const formatTime = (isoDate) => {
      const date = new Date(isoDate);
      return date.toLocaleTimeString(undefined, {
        hour: '2-digit',
        minute: '2-digit',
      });
    };
  
    const backgroundUrl = getBackgroundUrl();
  
    return (
      <div
        className="relative w-screen h-screen overflow-hidden"
        style={{
          backgroundImage: `url(${backgroundUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        {/* Top-left D2 Logo */}
        <img
          src="https://storage.googleapis.com/vicaassets/d2logo.png"
          alt="D2 Logo"
          style={{
            position: 'absolute',
            top: '12px',
            left: '12px',
            width: '120px',
            height: 'auto',
            zIndex: 50,
          }}
          className="md:w-[140px] w-[100px]"
        />
        {/* Hamburger Menu */}
        <div
          style={{
            position: 'absolute',
            top: '20px',
            right: '20px',
            zIndex: 60
          }}
        >
          <details style={{ cursor: 'pointer' }}>
            <summary
              style={{
                listStyle: 'none',
                width: '40px',
                height: '36px',
                backgroundColor: '#990000',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '24px',
                fontWeight: 'bold'
              }}
            >
              ☰
            </summary>
            <div
              style={{
                position: 'absolute',
                right: 0,
                marginTop: '10px',
	                background: 'white',
	                padding: '16px 20px',
	                borderRadius: '8px',
	                boxShadow: '0 3px 8px rgba(0,0,0,0.25)',
	                minWidth: '280px'
	              }}
	            >
	              <div
	                style={{
		                  display: 'flex',
		                  alignItems: 'center',
		                  justifyContent: 'flex-start',
		                  gap: '8px',
	                  color: '#222',
	                  fontSize: '16px',
	                  fontWeight: '700',
	                  marginBottom: '10px',
	                }}
	              >
	                <CloudQueueIcon fontSize="small" />
	                Resources
	              </div>
		              <div
		                style={{
		                  display: 'flex',
		                  flexDirection: 'column',
		                  alignItems: 'flex-start',
		                  gap: '10px',
		                  textAlign: 'left',
		                }}
		              >
		                {RESOURCE_LINKS.map((resource, index) => (
	                  <a
	                    key={resource.href}
	                    href={resource.href}
	                    target={resource.opensInNewTab ? '_blank' : undefined}
	                    rel={resource.opensInNewTab ? 'noopener noreferrer' : undefined}
	                    download={resource.download}
	                    style={{
	                      color: '#990000',
	                      fontSize: '15px',
		                      textDecoration: 'none',
		                      fontWeight: '600',
		                      lineHeight: 1.3,
		                      width: '100%',
		                      paddingBottom: index < RESOURCE_LINKS.length - 1 ? '10px' : 0,
		                      borderBottom:
		                        index < RESOURCE_LINKS.length - 1
		                          ? '1px solid #e0e0e0'
		                          : 'none',
		                    }}
	                  >
	                    {resource.label}
	                  </a>
	                ))}
	              </div>
	            </div>
	          </details>
	        </div>
        <div className="block md:hidden w-full h-full">
          {/* Top Half: Avatar */}
          <div className="w-full h-[50vh] overflow-hidden flex items-center justify-center">
            <D2TalkingHeadAvatar ref={talkingHeadRef} />
          </div>

          {/* Bottom Half: Chat */}
          <div className="w-full h-[50vh] bg-white/85 backdrop-blur-md p-4 rounded-t-xl flex flex-col">
            
            {/* Scrollable Messages */}
            <div className="flex-grow overflow-y-auto border border-gray-300 p-4 rounded-lg mb-2">
              {useMemo(() => {
                let lastDateHeader = null;
                const items = [];
                conversation.forEach((msg, idx) => {
                  const header = formatDateHeader(msg.createdAt);
                  if (header !== lastDateHeader) {
                    items.push(
                      <div
                        key={'header-' + idx}
                        className="text-center text-gray-600 my-2"
                      >
                        {header}
                      </div>
                    );
                    lastDateHeader = header;
                  }
                  items.push(
                    <div
                      key={'msg-' + idx}
                      className={`${
                        msg.role === 'user' ? 'text-right' : 'text-left'
                      } mb-4`}
                    >
                      <strong>{msg.role === 'user' ? 'You' : 'Avatar'}:</strong>{' '}
                      {msg.text}
                      <span className="text-xs text-gray-500 ml-2">
                        {formatTime(msg.createdAt)}
                      </span>
                    </div>
                  );
                });
                return items;
              }, [conversation])}
              {isLoading && (
                <div className="text-left mb-4 text-gray-700">
                  <strong>Avatar:</strong>
                  <span className="ml-2 inline-flex space-x-1">
                    <span className="w-2 h-2 bg-gray-500 rounded-full animate-bounce"></span>
                    <span className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></span>
                    <span className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></span>
                  </span>
                </div>
              )}
              <div ref={conversationEndRef} />
            </div>

            <div className="flex flex-col items-center w-full flex-shrink-0 mt-2 pb-2 bg-white/90 backdrop-blur-sm rounded-t-xl">
              
              {conversation.length === 0 && !sessionStarted ? (
                <button
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none"
                  onClick={handleStartSession}
                >
                  Start Session
                </button>
              ) : (
                <>
                  <div className="flex space-x-2 w-full">
                    <input
                      type="text"
                      className="w-[100%] px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring"
                      placeholder="Type your message..."
                      value={textInput}
                      onChange={(e) => setTextInput(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && textInput.trim()) {
                          sendMessageToBackend(textInput);
                          setTextInput('');
                        }
                      }}
                    />
                    <button
                      className="flex items-center justify-center p-3 bg-green-500 text-white rounded hover:bg-green-600 focus:outline-none disabled:opacity-50"
                      onClick={() => {
                        if (textInput.trim()) {
                          sendMessageToBackend(textInput);
                          setTextInput('');
                        }
                      }}
                      disabled={!textInput.trim() || isSpeaking}
                    >
                      <SendIcon />
                    </button>
                    <button
                      className={`flex items-center justify-center p-3 rounded text-white focus:outline-none disabled:opacity-50 ${
                        isListening ? 'bg-red-500 hover:bg-red-600' : 'bg-blue-500 hover:bg-blue-600'
                      }`}
                      onClick={handleToggleListen}
                      disabled={isSpeaking}
                      title={isListening ? 'Stop Listening' : 'Start Listening'}
                    >
                      <MicIcon />
                    </button>
                  </div>
                  {error && (
                    <div className="text-red-500 mt-1 text-center">{error}</div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        <div className="hidden md:flex w-full h-full">
          
          {/* Left side avatar */}
          <div className="w-1/2 h-full flex items-center justify-center">
            <D2TalkingHeadAvatar ref={talkingHeadRef} />
          </div>
        
          {/* Right side chat window */}
          <div className="w-1/2 h-full flex items-center justify-center p-8">
            <div className="w-[80%] h-[70%] bg-white/70 backdrop-blur-md rounded-xl border border-gray-300 p-6 flex flex-col">
        
              {/* Scrollable messages */}
              <div className="flex-grow overflow-y-auto mb-4 border border-gray-300 rounded-lg p-4">
                {useMemo(() => {
                  let lastDateHeader = null;
                  const items = [];
                  conversation.forEach((msg, idx) => {
                    const header = formatDateHeader(msg.createdAt);
                    if (header !== lastDateHeader) {
                      items.push(
                        <div key={'header-' + idx} className="text-center text-gray-600 my-2">
                          {header}
                        </div>
                      );
                      lastDateHeader = header;
                    }
                    items.push(
                      <div key={'msg-' + idx} className={`${msg.role === 'user' ? 'text-right' : 'text-left'} mb-3`}>
                        <strong>{msg.role === 'user' ? 'You' : 'Avatar'}:</strong> {msg.text}
                        <span className="text-xs text-gray-500 ml-2">{formatTime(msg.createdAt)}</span>
                      </div>
                    );
                  });
                  return items;
                }, [conversation])}
                {isLoading && (
                  <div className="text-left mb-4 text-gray-700">
                    <strong>Avatar:</strong>
                    <span className="ml-2 inline-flex space-x-1">
                      <span className="w-2 h-2 bg-gray-500 rounded-full animate-bounce"></span>
                      <span className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></span>
                      <span className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></span>
                    </span>
                  </div>
                )}
                <div ref={conversationEndRef} />
              </div>
        
              {/* Desktop input bar */}
              
              {conversation.length === 0 && !sessionStarted ? (
                <button
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none w-full"
                  onClick={handleStartSession}
                >
                  Start Session
                </button>
              ) : (
                <div className="flex space-x-2 w-full">
                  <input
                    type="text"
                    className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring"
                    placeholder="Type your message..."
                    value={textInput}
                    onChange={(e) => setTextInput(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && textInput.trim()) {
                        sendMessageToBackend(textInput);
                        setTextInput('');
                      }
                    }}
                  />
                  <button
                    className="p-3 bg-green-500 text-white rounded hover:bg-green-600 focus:outline-none disabled:opacity-50"
                    onClick={() => {
                      if (textInput.trim()) {
                        sendMessageToBackend(textInput);
                        setTextInput('');
                      }
                    }}
                    disabled={!textInput.trim() || isSpeaking}
                  >
                    <SendIcon />
                  </button>
                  <button
                    className={`p-3 rounded text-white focus:outline-none disabled:opacity-50 ${
                      isListening ? 'bg-red-500 hover:bg-red-600' : 'bg-blue-500 hover:bg-blue-600'
                    }`}
                    onClick={handleToggleListen}
                    disabled={isSpeaking}
                  >
                    <MicIcon />
                  </button>
                </div>
              )}
        
              {error && <div className="text-red-500 mt-2 text-center">{error}</div>}
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  export default D2Agent;
